/**
 * SidebarFlyout Hook
 *
 * Positions flyout content in a LiveView-owned portal outside sidebar overflow.
 * Uses @floating-ui/dom with fixed strategy for viewport-relative positioning.
 * Deferred hide prevents flicker when crossing the gap between trigger and flyout.
 * Keyboard: Arrow keys navigate items, Escape closes, Enter activates, Tab wraps.
 */

import { computePosition, flip, offset, shift } from '@floating-ui/dom'

// ── Types ──────────────────────────────────────────────────

type FlyoutElements = {
  readonly trigger: HTMLElement
  readonly content: HTMLElement
}

type FlyoutCleanup = () => void

type SidebarFlyoutHook = {
  readonly el: HTMLElement
  cleanup: FlyoutCleanup | null
}

type DeferredHide = {
  readonly schedule: () => void
  readonly cancel: () => void
}

// ── Constants ──────────────────────────────────────────────

const ITEM_SELECTOR = '[data-component=".sidebar-item"] > a'
const HIDE_DELAY_MS = 150

const POSITION_OPTIONS = {
  placement: 'right-start' as const,
  strategy: 'fixed' as const,
  middleware: [offset({ mainAxis: 8 }), flip(), shift({ padding: 8 })]
}

// ── Pure Predicates ────────────────────────────────────────

const isVisible = (el: HTMLElement): boolean => el.hasAttribute('data-open')

const isContainedIn = (
  container: HTMLElement,
  target: EventTarget | null
): boolean => target instanceof Node && container.contains(target)

// The portal owns the content; its stable trigger remains inside the sidebar.
const findElements = (content: HTMLElement): FlyoutElements | null => {
  const trigger = document.getElementById(content.dataset.flyoutTriggerId ?? '')

  return trigger instanceof HTMLElement ? { trigger, content } : null
}

const getFocusableItems = (container: HTMLElement): readonly HTMLElement[] =>
  Array.from(container.querySelectorAll(ITEM_SELECTOR)).filter(
    (el): el is HTMLElement => el instanceof HTMLElement
  )

const getFocusedIndex = (items: readonly HTMLElement[]): number =>
  items.indexOf(document.activeElement as HTMLElement)

const wrapIndex = (index: number, length: number): number =>
  length > 0 ? ((index % length) + length) % length : -1

// ── Effects ────────────────────────────────────────────────

const show = ({ trigger, content }: FlyoutElements): void => {
  computePosition(trigger, content, POSITION_OPTIONS).then(({ x, y }) => {
    content.style.left = `${x}px`
    content.style.top = `${y}px`
    content.setAttribute('data-open', '')
    trigger.setAttribute('data-open', '')
    getFocusableItems(content)[0]?.focus()
  })
}

const hide = ({ trigger, content }: FlyoutElements): void => {
  content.removeAttribute('data-open')
  trigger.removeAttribute('data-open')
}

const toggle = (elements: FlyoutElements): void =>
  isVisible(elements.content) ? hide(elements) : show(elements)

const moveFocus = (content: HTMLElement, offset: number): void => {
  const items = getFocusableItems(content)
  const next = wrapIndex(getFocusedIndex(items) + offset, items.length)
  items[next]?.focus()
}

/** Encapsulates deferred hide — schedule/cancel across the trigger↔flyout gap */
const createDeferredHide = (
  elements: FlyoutElements,
  delay: number
): DeferredHide => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  return {
    schedule: () => {
      if (timeoutId === null) {
        timeoutId = setTimeout(() => {
          hide(elements)
          timeoutId = null
        }, delay)
      }
    },
    cancel: () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }
  }
}

/** Builds a keydown handler with dispatch map — no switch, no early returns */
const createKeyHandler = (
  content: HTMLElement,
  elements: FlyoutElements,
  deferred: DeferredHide
): ((e: KeyboardEvent) => void) => {
  const actions = new Map<string, (e: KeyboardEvent) => void>([
    [
      'ArrowDown',
      (e) => {
        e.preventDefault()
        moveFocus(content, 1)
      }
    ],
    [
      'ArrowUp',
      (e) => {
        e.preventDefault()
        moveFocus(content, -1)
      }
    ],
    [
      'Tab',
      (e) => {
        e.preventDefault()
        moveFocus(content, e.shiftKey ? -1 : 1)
      }
    ],
    [
      'Home',
      (e) => {
        e.preventDefault()
        getFocusableItems(content)[0]?.focus()
      }
    ],
    [
      'End',
      (e) => {
        e.preventDefault()
        const items = getFocusableItems(content)
        items[items.length - 1]?.focus()
      }
    ],
    [
      'Escape',
      (e) => {
        e.preventDefault()
        deferred.cancel()
        hide(elements)
        elements.trigger.focus()
      }
    ]
  ])

  return (e: KeyboardEvent): void => {
    if (isVisible(content)) {
      actions.get(e.key)?.(e)
    }
  }
}

// ── Listener Setup (returns composable cleanup) ────────────

const setupListeners = (elements: FlyoutElements): FlyoutCleanup => {
  const { trigger, content } = elements

  const deferred = createDeferredHide(elements, HIDE_DELAY_MS)
  const onKeydown = createKeyHandler(content, elements, deferred)

  const onTriggerClick = (e: MouseEvent): void => {
    e.stopPropagation()
    deferred.cancel()
    toggle(elements)
  }

  const onClickOutside = (e: MouseEvent): void => {
    const inside =
      isContainedIn(trigger, e.target) || isContainedIn(content, e.target)
    if (!inside) {
      deferred.cancel()
      hide(elements)
    }
  }

  const onTriggerLeave = (e: MouseEvent): void => {
    if (!isContainedIn(content, e.relatedTarget)) {
      deferred.schedule()
    }
  }

  const onContentLeave = (e: MouseEvent): void => {
    if (!isContainedIn(trigger, e.relatedTarget)) {
      deferred.schedule()
    }
  }

  const onEnter = (): void => deferred.cancel()

  const onNavigation = (): void => {
    deferred.cancel()
    hide(elements)
  }

  trigger.addEventListener('click', onTriggerClick)
  document.addEventListener('click', onClickOutside)
  document.addEventListener('keydown', onKeydown)
  trigger.addEventListener('mouseleave', onTriggerLeave)
  trigger.addEventListener('mouseenter', onEnter)
  content.addEventListener('mouseleave', onContentLeave)
  content.addEventListener('mouseenter', onEnter)
  window.addEventListener('phx:page-loading-start', onNavigation)

  return () => {
    deferred.cancel()
    trigger.removeEventListener('click', onTriggerClick)
    document.removeEventListener('click', onClickOutside)
    document.removeEventListener('keydown', onKeydown)
    trigger.removeEventListener('mouseleave', onTriggerLeave)
    trigger.removeEventListener('mouseenter', onEnter)
    content.removeEventListener('mouseleave', onContentLeave)
    content.removeEventListener('mouseenter', onEnter)
    window.removeEventListener('phx:page-loading-start', onNavigation)
  }
}

// ── Hook ───────────────────────────────────────────────────

export const SidebarFlyout = {
  mounted(this: SidebarFlyoutHook) {
    const elements = findElements(this.el)
    this.cleanup = elements ? setupListeners(elements) : null
  },

  destroyed(this: SidebarFlyoutHook) {
    this.cleanup?.()
  }
}
